import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, ArrowUpRight, Instagram, Star } from 'lucide-react';
import { SITE, GOOGLE } from '@/content/site';
import { TID } from '@/constants/testIds';

const ease = [0.16, 1, 0.3, 1];

export default function Contact() {
  return (
    <section id="contact" className="py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease }}
        >
          <div className="text-overline mb-7">Get in Touch</div>
          <h2 className="font-display text-[1.75rem] sm:text-3xl lg:text-[2.5rem] leading-[1.15] max-w-3xl">
            Visit us in
            <span className="italic text-[hsl(var(--primary))] font-medium"> downtown Vancouver.</span>
          </h2>
          <p className="mt-7 max-w-2xl text-[hsl(var(--muted-foreground))] leading-relaxed">
            Walk-ins are welcome between bookings, but to guarantee a slot with your
            preferred artist, please book online via Fresha or give us a call.
          </p>
        </motion.div>

        <div className="mt-16 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Google Map */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.95, ease }}
            className="lg:col-span-7"
          >
            <div className="rounded-[1.5rem] overflow-hidden border border-[hsl(var(--hairline))] bg-[hsl(var(--secondary))] aspect-[4/3] sm:aspect-[16/10] luxe-shadow">
              <iframe
                title="Prép Beauty Parlour — 1305 Burrard Street, Vancouver"
                src={GOOGLE.mapEmbed}
                width="100%"
                height="100%"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{ border: 0 }}
                allowFullScreen
                data-testid="google-map-embed"
              />
            </div>

            <a
              data-testid="google-reviews-link"
              href={GOOGLE.reviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="luxe-hover-lift mt-7 inline-flex items-center gap-3 rounded-full border border-[hsl(var(--hairline))] bg-[hsl(var(--background))] px-5 py-3 hover:border-[hsl(var(--primary))] transition-colors group"
            >
              <span className="inline-flex items-center gap-2">
                <span className="font-display text-lg leading-none italic text-[hsl(var(--primary))]">G</span>
                <span className="inline-flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className="w-3 h-3 fill-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                      strokeWidth={1.25}
                    />
                  ))}
                </span>
              </span>
              <span className="text-[0.78rem] tracking-[0.08em]">
                <span className="font-medium">{GOOGLE.rating}</span>
                <span className="text-[hsl(var(--muted-foreground))]">
                  {' '}· {GOOGLE.reviewCount} Google reviews
                </span>
              </span>
              <ArrowUpRight className="w-3.5 h-3.5 ml-1 text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--primary))] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-500" strokeWidth={1.25} />
            </a>
          </motion.div>

          {/* Info column */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.95, ease, delay: 0.1 }}
            className="lg:col-span-5 space-y-10"
          >
            {[
              {
                icon: MapPin,
                title: 'Studio',
                body: (
                  <>
                    <div className="mt-2 text-[hsl(var(--muted-foreground))]">
                      {SITE.address.line1}
                      <br />
                      {SITE.address.line2}
                    </div>
                    <div className="mt-1.5 text-[0.6rem] uppercase tracking-[0.32em] text-[hsl(var(--muted-foreground))] font-light">
                      {SITE.address.neighbourhoods}
                    </div>
                    <a
                      data-testid="contact-directions"
                      href={SITE.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-1.5 text-[0.72rem] tracking-[0.24em] uppercase font-light text-[hsl(var(--primary))] hover:gap-3 transition-all duration-500"
                    >
                      Get directions <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.25} />
                    </a>
                  </>
                ),
              },
              {
                icon: Phone,
                title: 'Phone',
                body: (
                  <>
                    <a
                      data-testid="contact-phone"
                      href={SITE.phoneHref}
                      className="mt-2 block text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors"
                    >
                      {SITE.phone}
                    </a>
                    <div className="mt-1.5 text-[0.78rem] text-[hsl(var(--muted-foreground))]/75">
                      Call or text — we respond within business hours.
                    </div>
                  </>
                ),
              },
              {
                icon: Mail,
                title: 'Email',
                body: (
                  <a
                    data-testid="contact-email"
                    href={`mailto:${SITE.email}`}
                    className="mt-2 block text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors break-all"
                  >
                    {SITE.email}
                  </a>
                ),
              },
              {
                icon: Clock,
                title: 'Hours',
                body: (
                  <ul className="mt-3 space-y-2 text-[hsl(var(--muted-foreground))]">
                    {SITE.hours.map((h) => (
                      <li key={h.day} className="flex justify-between gap-6 text-[0.9rem]">
                        <span>{h.day}</span>
                        <span className="tabular">{h.time}</span>
                      </li>
                    ))}
                  </ul>
                ),
              },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex items-start gap-5">
                <Icon className="w-4 h-4 mt-1.5 text-[hsl(var(--primary))] shrink-0" strokeWidth={1.25} />
                <div className="w-full">
                  <div className="font-display text-[1.25rem] tracking-tight">{title}</div>
                  {body}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* CTA strip */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.95, ease }}
          className="mt-20 lg:mt-24 rounded-[1.75rem] bg-[hsl(var(--warm-ink))] text-[hsl(var(--cream))] p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-14 grain relative overflow-hidden"
        >
          <div className="flex-1 relative z-10">
            <div className="text-[0.65rem] uppercase tracking-[0.32em] text-[hsl(var(--champagne))]/70 mb-5 font-light">
              Book Online
            </div>
            <h3 className="font-display text-[1.7rem] lg:text-[2.2rem] tracking-tight leading-tight max-w-lg">
              Book in under
              <span className="italic"> 60 seconds.</span>
            </h3>
            <p className="mt-4 text-[0.92rem] text-[hsl(var(--cream))]/70 leading-[1.8] max-w-lg">
              See live availability with Roselyn, Maryna or Miriam. New clients save 15%
              with code <span className="text-[hsl(var(--champagne))] tracking-[0.2em]">{SITE.promoCode}</span>.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 lg:gap-4 relative z-10">
            <a
              data-testid="contact-book-btn"
              href={SITE.booking}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-luxe inline-flex items-center gap-2.5 rounded-full bg-[hsl(var(--cream))] text-[hsl(var(--warm-ink))] px-8 py-4 text-[0.72rem] tracking-[0.24em] uppercase font-light hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--cream))] transition-colors duration-700"
            >
              Book on Fresha
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.25} />
            </a>
            <a
              data-testid="contact-google-review-btn"
              href={GOOGLE.writeReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--cream))]/25 text-[hsl(var(--cream))]/85 px-8 py-4 text-[0.72rem] tracking-[0.24em] uppercase font-light hover:border-[hsl(var(--cream))] hover:text-[hsl(var(--cream))] transition-colors duration-500"
            >
              <Star className="w-3 h-3 fill-[hsl(var(--cream))]/80 text-[hsl(var(--cream))]/80" strokeWidth={1.25} />
              Leave a Google review
            </a>
            <a
              data-testid={TID.footerInstagram + '-contact'}
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.32em] text-[hsl(var(--cream))]/65 hover:text-[hsl(var(--cream))] font-light"
            >
              <Instagram className="w-3.5 h-3.5" strokeWidth={1.25} />
              @prepbeautyparlour
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
